import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Save, Plus, Trash2, UserPlus, X, MapPin } from "lucide-react";
import { api, formatApiErrorDetail, formatINR, formatCompactNumber } from "@/lib/api";

const CATEGORIES = ["Luxury", "Premium", "Mainstream"];
const TIERS = ["Tier 1", "Tier 2", "Tier 3"];
const STATUSES = ["draft", "live", "archived"];

export default function AdminMallDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mall, setMall] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [showSpace, setShowSpace] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/super-admin/malls/${id}`);
      setMall(data);
      setForm({
        name: data.name || "", city: data.city || "", state: data.state || "",
        tier: data.tier || "Tier 1", category: data.category || "Premium",
        monthly_footfall: data.monthly_footfall || 0, area_sqft: data.area_sqft || "",
        floors: data.floors || "", address: data.address || "", opened_year: data.opened_year || "",
        description: data.description || "", catchment: data.catchment || "",
        status: data.status || "draft",
        brands: (data.brands || []).join(", "),
        owners: (data.owners || []).join(", "),
        hero_image: data.hero_image || "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      nav("/admin/malls");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        area_sqft: form.area_sqft ? Number(form.area_sqft) : null,
        floors: form.floors ? Number(form.floors) : null,
        opened_year: form.opened_year ? Number(form.opened_year) : null,
        monthly_footfall: Number(form.monthly_footfall) || 0,
        brands: form.brands ? form.brands.split(",").map((s) => s.trim()).filter(Boolean) : [],
        owners: form.owners ? form.owners.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.patch(`/super-admin/malls/${id}`, payload);
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function ingest() {
    setAiRunning(true);
    try {
      const { data } = await api.post(`/super-admin/malls/${id}/ai-ingest`, {});
      const fields = Object.keys(data.applied || {}).length;
      toast.success(`AI filled ${fields} fields`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI ingest failed");
    } finally { setAiRunning(false); }
  }

  async function delSpace(spaceId) {
    if (!window.confirm("Delete this ad space?")) return;
    try {
      await api.delete(`/super-admin/spaces/${spaceId}`);
      toast.success("Space deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function unassign(uid) {
    if (!window.confirm("Remove this mall admin from this mall?")) return;
    try {
      await api.post(`/super-admin/malls/${id}/unassign`, { user_id: uid });
      toast.success("Removed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  if (!mall) return <div className="text-neutral-500">Loading…</div>;

  return (
    <div data-testid="admin-mall-detail">
      <Link to="/admin/malls" className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-amber-500">
        <ArrowLeft size={12} /> All malls
      </Link>

      <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline">Mall · {mall.status}</div>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl tracking-tighter">{mall.name}</h1>
          <div className="mt-2 text-neutral-400 text-sm flex items-center gap-1"><MapPin size={12} /> {mall.city}, {mall.state || "India"}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={ingest} disabled={aiRunning} className="btn-secondary" data-testid="ai-ingest-btn">
            <Sparkles size={14} /> {aiRunning ? "AI ingesting…" : "AI Ingest"}
          </button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-mall-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface p-6">
          <div className="overline">Mall details</div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Mall name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="md-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
            <Field label="Tier">
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Monthly footfall"><input type="number" value={form.monthly_footfall} onChange={(e) => setForm({ ...form, monthly_footfall: e.target.value })} /></Field>
            <Field label="Catchment"><input value={form.catchment} onChange={(e) => setForm({ ...form, catchment: e.target.value })} /></Field>
            <Field label="Area (sqft)"><input type="number" value={form.area_sqft} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} /></Field>
            <Field label="Floors"><input type="number" value={form.floors} onChange={(e) => setForm({ ...form, floors: e.target.value })} /></Field>
            <Field label="Opened year"><input type="number" value={form.opened_year} onChange={(e) => setForm({ ...form, opened_year: e.target.value })} /></Field>
            <Field label="Hero image URL"><input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} /></Field>
            <Field label="Address" full><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Brands (comma-separated)" full><input value={form.brands} onChange={(e) => setForm({ ...form, brands: e.target.value })} /></Field>
            <Field label="Owners (comma-separated)" full><input value={form.owners} onChange={(e) => setForm({ ...form, owners: e.target.value })} /></Field>
            <Field label="Description" full><textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface p-6">
            <div className="overline">At a glance</div>
            <div className="mt-4 space-y-3 text-sm">
              <Glance label="Ad spaces" value={mall.spot_count || 0} />
              <Glance label="Footfall (mo)" value={formatCompactNumber(mall.monthly_footfall || 0)} />
              <Glance label="Brands" value={(mall.brands || []).length} />
              <Glance label="Owners" value={(mall.owners || []).length} />
            </div>
          </div>

          <div className="surface p-6">
            <div className="flex items-center justify-between">
              <div className="overline">Assigned mall admins</div>
              <button onClick={() => setShowAssign(true)} className="text-amber-500 text-xs hover:underline inline-flex items-center gap-1" data-testid="assign-admin-btn">
                <UserPlus size={12} /> Assign
              </button>
            </div>
            {(mall.assigned_admins || []).length === 0 ? (
              <p className="mt-4 text-xs text-neutral-500">No mall admins assigned yet.</p>
            ) : (
              <ul className="mt-4 space-y-2">
                {mall.assigned_admins.map((u) => (
                  <li key={u.id} className="flex items-start justify-between gap-3 border-b border-white/5 pb-3 last:border-none">
                    <div>
                      <div className="text-sm font-medium">{u.name}</div>
                      <div className="text-[11px] text-neutral-500">{u.email} · {u.status}</div>
                    </div>
                    <button onClick={() => unassign(u.id)} className="text-neutral-500 hover:text-red-400"><X size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Formats & spaces */}
      <div className="mt-10">
        <div className="flex items-end justify-between">
          <div>
            <div className="overline">Ad formats & spaces</div>
            <h2 className="mt-2 font-display text-2xl">{mall.spot_count || 0} spaces · {(mall.formats || []).length} formats</h2>
          </div>
          <button onClick={() => setShowSpace(true)} className="btn-secondary" data-testid="add-space-btn">
            <Plus size={14} /> Add space
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {(mall.formats || []).length === 0 && (
            <div className="surface p-10 text-center text-neutral-500">No ad spaces yet for this mall.</div>
          )}
          {(mall.formats || []).map((f) => (
            <details key={f.name} className="surface p-5 group" open={f.count <= 12}>
              <summary className="cursor-pointer flex items-center justify-between">
                <div>
                  <span className="font-display text-lg">{f.name}</span>
                  <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-amber-500">{f.count} spaces</span>
                </div>
              </summary>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-white/10">
                    <tr>
                      <th className="p-2">Placement</th>
                      <th className="p-2">Dimensions</th>
                      <th className="p-2">Footfall</th>
                      <th className="p-2">Price / week</th>
                      <th className="p-2">Status</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.spaces.map((s) => (
                      <tr key={s.id} className="border-b border-white/5">
                        <td className="p-2">{s.placement}</td>
                        <td className="p-2 text-neutral-400 text-xs">{s.dimensions || "—"}</td>
                        <td className="p-2 text-neutral-400 text-xs font-mono">{formatCompactNumber(s.monthly_footfall || 0)}</td>
                        <td className="p-2 text-amber-500 text-xs whitespace-nowrap">{formatINR(s.price_per_week || 0)}</td>
                        <td className="p-2"><span className="text-[10px] uppercase tracking-[0.2em] border border-white/15 px-2 py-0.5">{s.availability}</span></td>
                        <td className="p-2 text-right">
                          <button onClick={() => delSpace(s.id)} className="text-neutral-500 hover:text-red-400"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </div>
      </div>

      {showSpace && <CreateSpaceModal mallId={id} onClose={() => { setShowSpace(false); load(); }} />}
      {showAssign && <AssignAdminModal mallId={id} onClose={() => { setShowAssign(false); load(); }} />}
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Glance({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-2 last:border-none">
      <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
      <span className="font-display text-lg">{value}</span>
    </div>
  );
}

function CreateSpaceModal({ mallId, onClose }) {
  const [form, setForm] = useState({
    format: "", format_category: "Static", placement: "", dimensions: "",
    monthly_footfall: 0, price_per_week: 0, availability: "Available", description: "",
  });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.format || !form.placement) return toast.error("Format and placement required");
    setSaving(true);
    try {
      await api.post(`/super-admin/malls/${mallId}/spaces`, {
        ...form,
        monthly_footfall: Number(form.monthly_footfall) || 0,
        price_per_week: Number(form.price_per_week) || 0,
      });
      toast.success("Space added");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add ad space" onClose={onClose} testId="create-space-modal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Format (e.g. Atrium, LED Screen)"><input value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} /></Field>
        <Field label="Format category">
          <select value={form.format_category} onChange={(e) => setForm({ ...form, format_category: e.target.value })}>
            <option>Static</option><option>DOOH</option><option>Experiential</option><option>Ambient</option>
          </select>
        </Field>
        <Field label="Placement"><input value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} /></Field>
        <Field label="Dimensions"><input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} /></Field>
        <Field label="Monthly footfall"><input type="number" value={form.monthly_footfall} onChange={(e) => setForm({ ...form, monthly_footfall: e.target.value })} /></Field>
        <Field label="Price / week (INR)"><input type="number" value={form.price_per_week} onChange={(e) => setForm({ ...form, price_per_week: e.target.value })} /></Field>
        <Field label="Status">
          <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })}>
            <option>Available</option><option>Limited</option><option>Booked</option>
          </select>
        </Field>
        <Field label="Description" full><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button onClick={onClose} className="text-sm text-neutral-400 hover:text-white px-4 py-2">Cancel</button>
        <button onClick={submit} disabled={saving} className="btn-primary" data-testid="space-save">{saving ? "Saving…" : "Add space"}</button>
      </div>
    </Modal>
  );
}

function AssignAdminModal({ mallId, onClose }) {
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/super-admin/users", { params: { role: "mall_owner" } }).then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  async function assignExisting() {
    if (!pick) return toast.error("Pick a user");
    setSaving(true);
    try {
      await api.post(`/super-admin/malls/${mallId}/assign`, { user_id: pick });
      toast.success("Assigned");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function createAndAssign() {
    if (!newUser.name || !newUser.email || newUser.password.length < 8) {
      return toast.error("Name, email, and password (min 8) required");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/super-admin/users", {
        ...newUser, role: "mall_owner", status: "active", mall_ids: [mallId],
      });
      await api.post(`/super-admin/malls/${mallId}/assign`, { user_id: data.id });
      toast.success("Mall admin created and assigned");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Assign mall admin" onClose={onClose} testId="assign-admin-modal">
      {!creating ? (
        <>
          <Field label="Pick an existing mall admin">
            <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="assign-pick">
              <option value="">— select —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </Field>
          <div className="mt-6 flex justify-between items-center">
            <button onClick={() => setCreating(true)} className="text-xs text-amber-500 hover:underline">+ Create new mall admin</button>
            <div className="flex gap-3">
              <button onClick={onClose} className="text-sm text-neutral-400 hover:text-white px-4 py-2">Cancel</button>
              <button onClick={assignExisting} disabled={saving} className="btn-primary" data-testid="assign-submit">{saving ? "Assigning…" : "Assign"}</button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name"><input data-testid="newuser-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} /></Field>
            <Field label="Email"><input type="email" data-testid="newuser-email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} /></Field>
            <Field label="Temporary password (min 8)" full><input type="text" data-testid="newuser-pwd" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} /></Field>
          </div>
          <div className="mt-6 flex justify-between items-center">
            <button onClick={() => setCreating(false)} className="text-xs text-neutral-400 hover:underline">← Pick existing instead</button>
            <div className="flex gap-3">
              <button onClick={onClose} className="text-sm text-neutral-400 hover:text-white px-4 py-2">Cancel</button>
              <button onClick={createAndAssign} disabled={saving} className="btn-primary" data-testid="newuser-submit">{saving ? "Creating…" : "Create + assign"}</button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

function Modal({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid={testId}>
      <div className="surface max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}
